import styles from './EditTable.module.scss';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import { Box, Stack, Theme, useMediaQuery } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import clsx from 'clsx';
import { isEqual, omit } from 'lodash';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Column,
  ColumnInstance,
  HeaderGroup,
  IdType,
  Renderer,
  TableInstance,
  useBlockLayout,
  useColumnOrder,
  useResizeColumns,
  useRowSelect,
  useTable,
} from 'react-table';
import { AutoSizer } from 'react-virtualized';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import { ColumnAdditionalData } from '@work4all/components';
import { ResizableArea } from '@work4all/components/lib/components/ResizableArea';
import {
  EditableCell,
  EditableCellProps,
} from '@work4all/components/lib/dataDisplay/basic-table/components/row-render/components/editable-cell/EditableCell';
import { getExactColumnWidth } from '@work4all/components/lib/dataDisplay/basic-table/components/table-header/components/header-cell/HeaderCell';
import { TableHeader } from '@work4all/components/lib/dataDisplay/basic-table/components/table-header/TableHeader';
import { useSelectionRowClick } from '@work4all/components/lib/dataDisplay/basic-table/hooks/use-selection-row-click';
import {
  CellEditHandler,
  EditModeConfig,
  EditModeTableInstance,
  useEditMode,
} from '@work4all/components/lib/dataDisplay/basic-table/plugins/useEditMode';
import { SELECTION_COLUMN_ID } from '@work4all/components/lib/dataDisplay/basic-table/utils/makeRowsSelectable';
import { ExpandButton } from '@work4all/components/lib/input/expand-button/ExpandButton';
import { NavigationOverlay } from '@work4all/components/lib/navigation/navigation-overlay';

import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { reactRefSetter } from '@work4all/utils/lib/reactRefSetter';

import { ERP_EXPAND_COLUMN_ID } from '../../../../../../../../../../components/data-tables/generic-column/GenericColumns';
import { settings, useSetting } from '../../../../../../../../../../settings';

import { applyCustomTableHooks } from './apply-custom-table-hooks';
import { TableCell, TableHeaderRow, TableRow } from './components';
import {
  EditTableProvider,
  useEditTable,
} from './components/edit-table-provider/EditTableProvider';
import { TableCellContainer } from './components/table-cell-container/TableCellContainer';
import { genericMemo } from './generic-memo';
import { EditRowSelectionProvider } from './hooks/use-edit-row-selection';
import { useEditTableAllCells } from './hooks/use-edit-table-all-cells';
import { useEditableColumnsSettings } from './hooks/use-editable-columns-settings';
import { useEditableRow } from './hooks/use-editable-row';
import { InfiniteList, InfiniteListState, InfiniteRow } from './InfiniteList';
import {
  EditTableEntry,
  EditTableProps,
  EditTablePropsAll,
  IEditTable,
} from './types';

const CONST_OBJECT = {};
const CONST_ARRAY = [];
function EditTableInternal<T extends EditTableEntry>(
  props: EditTableProps<T>,
  ref?: React.ForwardedRef<IEditTable>
) {
  const {
    disabled = false,
    fitSpace = false,
    items,
    onSelectedItemIdsChange,
    onSelectedItemsChange,
    onDropItem,
    tableInstanceRef,
    columns,
    mask: Mask,
    editableRows = CONST_OBJECT,
    renderMobileItem,
    renderMobileFooter,
    classes: overrideClasses,
    loadingRows = CONST_ARRAY,
    noIdEditableCells = CONST_ARRAY,
    itemSize = 'auto',
    decorators,
    showBorders = true,
    showSelectionColumn = false,
    showSelectionColumnOnInit = false,
    allowedColumns,
    onRowOut,
    columnSettings,
    rowSizeMode,
    handleOrderChange,
    entity,
    bordersKey = 'STANDARD',
    updateMode = 'row',
    defaultFocusedColumn = 'amount',
    tableMaxHeight,
    hideRelationMarkers,
    hideCheckboxOnChildren,
    isRowEditable = true,
  } = props;

  const tablesLayoutBorders = useSetting(
    settings.tablesLayoutBorders(bordersKey)
  );

  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const classes =
    bordersKey === 'ERP'
      ? overrideClasses
      : {
          table: styles['classes-table'],
          thead: isMobile && styles['classes-thead'],
          tbody: !tablesLayoutBorders.value.vertical
            ? styles['classes-tbody']
            : undefined,
          tfoot: clsx({
            [styles['classes-tfoot']]:
              tablesLayoutBorders.value.vertical &&
              tablesLayoutBorders.value.horizontal,
            [styles['classes-tfootVertical']]:
              tablesLayoutBorders.value.vertical &&
              !tablesLayoutBorders.value.horizontal,
            [styles['classes-tfootHorizontal']]:
              !tablesLayoutBorders.value.vertical,
          }),
          ...overrideClasses,
        };

  const allCells = useEditTableAllCells<T>(columns);

  const defaultColumn: Partial<Column<T>> & {
    EditableCell: Renderer<Partial<EditableCellProps>>;
  } = useMemo(
    () => ({
      EditableCell: (cellProps) => (
        <EditableCell {...cellProps} stopPropagation />
      ),
    }),
    []
  );

  const { set: setTableRightAreaVisible, value: tableRightAreaVisible } =
    useSetting(
      settings.tableRightAreaVisible({
        entityType: entity,
      })
    );

  const {
    onEditPosition,
    onCollapsePosition,
    onMovePosition,
    dragHandlerVisible,
    dragHandlerClickable,
  } = useEditTable();

  const { t } = useTranslation();

  const data = useRef<T[]>([]);
  const listRef = useRef<VariableSizeList>(null);

  useMemo(() => {
    data.current = items;
  }, [items]);

  // update height of rows
  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [items, itemSize]);

  function getRowId(position: T) {
    return position.localId;
  }

  // aggregate this events if it's same row
  const rowInEditModeValues = useRef<T>(null);
  const onRowEdit = useCallback(() => {
    if (!onEditPosition) return;
    if (!rowInEditModeValues.current || !rowInEditModeValues.current.localId) {
      console.error('There is no position');
      return;
    }
    const onEditPositionArgs = {
      position: rowInEditModeValues.current,
    };
    onEditPosition(onEditPositionArgs);
    rowInEditModeValues.current = null;
  }, [onEditPosition]);

  const onCellEdit: CellEditHandler<T> = useCallback(
    (result) => {
      const { cell, value } = result;
      const row = cell.row;
      const rowOriginal = row.original;

      if (!isRowEditable) return;

      rowInEditModeValues.current = {
        ...rowInEditModeValues.current,
        ...{
          id: rowOriginal.id,
          [cell.column.id]: value,
          localId: row.id,
        },
      };
      const dirtyFields = omit(rowInEditModeValues.current, 'id');
      rowOriginal.dirtyFields = dirtyFields;
      Object.entries(dirtyFields).forEach(
        (field) => (rowOriginal[field[0]] = field[1])
      );
      if (updateMode === 'cell' && result.interaction === 'blur-update') {
        onRowEdit();
      }
    },
    [updateMode, onRowEdit, isRowEditable]
  );

  // save all changes when unmout position list
  useEffect(() => {
    return () => {
      onRowEdit();
    };
  }, [onRowEdit]);

  const hiddenColumns = useMemo(() => {
    if (!columnSettings) return [];

    if (allowedColumns) {
      return columns
        .filter((col) => !allowedColumns.includes(col.accessor as string))
        .map((col) => col.accessor as string);
    }

    const _hiddenColumns = columns
      .filter(
        (col) =>
          !columnSettings.value.visibility.includes(String(col.accessor)) &&
          col.accessor !== ERP_EXPAND_COLUMN_ID
      )
      .map((col) => col.accessor as string);

    if (showSelectionColumnOnInit) return _hiddenColumns;
    return [..._hiddenColumns, SELECTION_COLUMN_ID];
  }, [columns, columnSettings, allowedColumns, showSelectionColumnOnInit]);

  const lastEmittedSelectedItemIds = useRef<Record<IdType<T>, boolean>>(
    {} as Record<IdType<T>, boolean>
  );

  const tableInstance = useTable<T>(
    {
      columns,
      defaultColumn,
      data: items,
      getRowId,
      onCellEdit,
      initialState: {
        hiddenColumns,
        selectedRowIds:
          `{}` === JSON.stringify(lastEmittedSelectedItemIds.current) &&
          props.initialState?.selectedRowIds
            ? props.initialState.selectedRowIds
            : lastEmittedSelectedItemIds.current,
      },
    },
    useBlockLayout,
    useColumnOrder,
    useRowSelect,
    useResizeColumns,
    useEditMode,

    (hooks) => {
      applyCustomTableHooks<T>(hooks, {
        onSelectedItemIdsChange,
        showSelectionColumn,
        rows: tableInstanceRef.current?.rows,
        decorators,
        hideCheckboxOnChildren,
        onCollapsePosition,
        showBorders,
      });
    }
  ) as TableInstance<T> & EditModeTableInstance;

  const {
    headerGroups,
    getTableBodyProps,
    getTableProps,
    prepareRow,
    rows,
    setEditModeConfig,
    state: { selectedRowIds, groupBy },
    visibleColumns,
    setColumnOrder,
  } = tableInstance;

  useEditableColumnsSettings({ tableInstance, allowedColumns, columnSettings });

  useEffect(() => {
    reactRefSetter(tableInstanceRef)(tableInstance);

    return () => {
      reactRefSetter(tableInstanceRef)(null);
    };
  }, [tableInstanceRef, tableInstance]);

  useEffect(() => {
    if (!isEqual(selectedRowIds, lastEmittedSelectedItemIds.current)) {
      const selectedIds = Object.entries(selectedRowIds)
        .filter(([, selected]) => selected)
        .map(([id]) => id);

      const localIds = data.current.filter((item) =>
        selectedIds.includes(item.localId)
      );
      lastEmittedSelectedItemIds.current = localIds
        .map((withLocalId) => withLocalId.localId)
        .reduce((acc, p) => {
          acc[p] = true;
          return acc;
        }, {} as Record<string, boolean>) as Record<IdType<T>, boolean>;
      onSelectedItemIdsChange?.(selectedIds);
      const selectedPositions = rows
        ?.filter((row) => selectedIds.includes(row.id))
        ?.map((row) => row.original);
      onSelectedItemsChange?.(selectedPositions);
    }
  }, [
    lastEmittedSelectedItemIds,
    onSelectedItemsChange,
    onSelectedItemIdsChange,
    selectedRowIds,
  ]);

  const { calculateEditableCols, calculateRowProperties } = useEditableRow({
    allCells,
    data,
    decorators,
    editableRows,
    onMovePosition,
    onBeforeDrag: () => {
      if (rowInEditModeValues.current && onEditPosition) {
        onRowEdit();
      }
    },
  });

  const localScrollRef = useRef<HTMLDivElement>(null);

  const setEditMode = useCallback(
    (
      rowId: string,
      autofocus?: EditModeConfig['autoFocus'],
      columns?: EditModeConfig['columns']
    ) => {
      const row = tableInstanceRef.current.rows.find(
        (row) => String(row.original.id) === rowId || row.id === rowId
      );
      if (row) {
        if (!columns) {
          const { editableCols } = calculateEditableCols(row.original);
          columns = editableCols;
        }
        tableInstanceRef.current.setEditModeConfig({
          columns,
          row: row.id,
          autoFocus: autofocus,
        });
        tableInstanceRef.current.toggleAllRowsSelected(false);
        tableInstanceRef.current.toggleRowSelected(row.id, true);
      }
    },
    [calculateEditableCols, tableInstanceRef]
  );

  const handleOnRowOut = useCallback(
    (position: T, dir: 1 | -1) => {
      const positionIndex = items.findIndex((item) => item.id === position?.id);
      const nextPosition = items[positionIndex + dir];
      if (nextPosition) setEditMode(nextPosition.id.toString(), allCells[0]);
      // scroll to the left to see longtext popover
      if (localScrollRef.current) localScrollRef.current.scrollLeft = 0;
    },
    [items, localScrollRef, setEditMode, allCells]
  );

  useImperativeHandle(
    ref,
    () => {
      return {
        scrollTo: (to?: number, column?: string) => {
          // Save any pending edits before scrolling to a new item
          if (rowInEditModeValues.current && onEditPosition) {
            onRowEdit();
          }

          const tableInstance = tableInstanceRef.current as TableInstance<T> &
            EditModeTableInstance;

          if (to === undefined) {
            to = items.length - 1;
          }
          listRef.current?.scrollToItem(to);

          // make new row editable
          const row = tableInstance.rows[to];
          if (row) {
            const { editableCols } = calculateEditableCols(row.original);
            const columns = editableCols;
            tableInstance.setEditModeConfig({
              columns,
              row: row.id,
              autoFocus: editableCols.includes(column ?? defaultFocusedColumn)
                ? column ?? defaultFocusedColumn
                : editableCols[0],
            });
            tableInstance.toggleAllRowsSelected(false);
            tableInstance.toggleRowSelected(row.id, true);
          }
        },
        setEditMode,
      };
    },
    [
      calculateEditableCols,
      items,
      setEditMode,
      tableInstanceRef,
      defaultFocusedColumn,
      rowInEditModeValues,
      onEditPosition,
      onRowEdit,
    ]
  );

  const onClickOutSide = useEventCallback((localId: string) => {
    if (
      rowInEditModeValues.current &&
      rowInEditModeValues.current.localId === localId
    ) {
      if (!localId) {
        setEditModeConfig({
          columns: [],
          row: '',
        });
        return;
      }

      onRowEdit();
      setEditModeConfig({
        columns: [],
        row: '',
      });
    }
  });
  const skeletonFirstRowAffectedIndex = items.findIndex(
    (item) => item.id === loadingRows[0]
  );
  const tableBodyRef = useRef<HTMLDivElement>(null);

  const onRowClick = useSelectionRowClick<T>({
    tableBodyRef,
    tableInstance,
    ctrlOnly: true,
    selectableMultiple: true,
    selectableRows: true,
  });

  const shouldAutoSizeRows = itemSize === 'auto';

  const renderRow = useCallback(
    (
      props: ListChildComponentProps<InfiniteRow<T>[]>,
      state: InfiniteListState,
      measureRef: React.RefCallback<HTMLElement>,
      maxRowSize: number
    ) => {
      const { isRowVisible } = state;
      const { index, data: rowData, style } = props;
      const row = rowData[index];

      prepareRow(row);
      const {
        canDrop,
        getHovered,
        isMoveAllowed,
        getCurrent,
        handleMoveRow,
        isParentSelected,
        editableCols,
        isNormal,
        isParent,
        isChild,
      } = calculateRowProperties(row, rowData);

      const renderRow = () => {
        switch (row.original.positionKind) {
          case ErpPositionsKind.SEITENUMBRUCH:
            return (
              <TableRow
                ref={measureRef}
                localId={row.original.localId}
                disabled={disabled}
                index={index}
                isSimpleMode={!showBorders}
                canDrop={canDrop}
                isNormal={isNormal}
                onMoveRow={handleMoveRow}
                isMenuVisible={!!onMovePosition}
                isDragHandlerVisible={
                  !dragHandlerVisible || dragHandlerVisible(row.original)
                }
                isDragHandlerClickable={
                  !dragHandlerClickable || dragHandlerClickable(row.original)
                }
                kind={row.original.positionKind}
                onClick={(e) => {
                  e.stopPropagation();
                  onRowClick(e, row);
                }}
                {...row.getRowProps({
                  className: clsx(styles['tr-border'], classes?.tableRow, {
                    [styles.selectedRow]: row.isSelected,
                  }),
                  style: {
                    ...style,
                    ...(shouldAutoSizeRows && {
                      height: 'auto',
                      maxHeight: maxRowSize,
                    }),
                  },
                })}
                key={row.original.localId}
              >
                {showBorders ? (
                  <>
                    <TableCell
                      {...row.cells[0].getCellProps()}
                      className={styles['table-cell']}
                    >
                      {row.cells[0].render('Cell')}
                    </TableCell>
                    <div className={styles['page-break']}>
                      <InsertPageBreakIcon />
                      <div>{t('COMMON.PAGE_BREAK')}</div>
                    </div>
                  </>
                ) : (
                  row.cells.map((cell, idx) => {
                    return (
                      <TableCell
                        key={cell.column.id}
                        {...cell.getCellProps()}
                        className={clsx(styles['table-cell'], {
                          [styles['border-break']]:
                            cell.column.id !== 'PLACEHOLDER',
                        })}
                      >
                        {idx === 2 ? (
                          <div className={styles['page-break']}>
                            <InsertPageBreakIcon />
                            <div>{t('COMMON.PAGE_BREAK')}</div>
                          </div>
                        ) : (
                          <></>
                        )}
                      </TableCell>
                    );
                  })
                )}
              </TableRow>
            );
          default:
            return (
              <TableRow
                ref={measureRef}
                localId={row.original.localId}
                canDrop={canDrop}
                getHovered={getHovered}
                isMoveAllowed={isMoveAllowed}
                getCurrent={getCurrent}
                isSimpleMode={!showBorders}
                isMenuVisible={!!onMovePosition}
                isDragHandlerVisible={
                  !dragHandlerVisible || dragHandlerVisible(row.original)
                }
                isDragHandlerClickable={
                  !dragHandlerClickable || dragHandlerClickable(row.original)
                }
                className={clsx(classes?.tableRow, {
                  [styles.selectedRow]: row.isSelected,
                  [styles['parent-selected']]: isParentSelected,
                  [styles['relationParent']]:
                    isParent && !row.original.collapsed,
                  [styles['relationChild']]: isChild && !hideRelationMarkers,
                })}
                disabled={disabled}
                index={index}
                onMoveRow={row.original.cacheOnly ? undefined : handleMoveRow}
                kind={row.original.positionKind}
                onClickOutside={onClickOutSide}
                isNormal={isNormal}
                hasBorder={showBorders ? tablesLayoutBorders.value : undefined}
                onClick={(e) => {
                  onRowClick(e, row);
                }}
                {...row.getRowProps({
                  style: {
                    ...style,
                    ...(shouldAutoSizeRows && {
                      height: 'auto',
                      maxHeight: maxRowSize,
                    }),
                  },
                })}
                key={row.original.localId}
              >
                <TableCellContainer
                  key={row.original.localId}
                  editableCols={editableCols}
                  index={index}
                  isRowVisible={isRowVisible}
                  listRef={listRef}
                  onRowEdit={onEditPosition ? onRowEdit : null}
                  row={row}
                  rowInEditModeValues={rowInEditModeValues}
                  setEditModeConfig={setEditModeConfig}
                  rowSizeMode={rowSizeMode}
                  skeletonFirstRowAffectedIndex={skeletonFirstRowAffectedIndex}
                  tableInstance={tableInstance}
                  columns={columns}
                  tablesLayoutBorders={tablesLayoutBorders}
                  disabled={disabled}
                  onRowOut={onRowOut ?? handleOnRowOut}
                  loadingRows={loadingRows}
                  showBorders={showBorders}
                  noIdEditableCells={noIdEditableCells}
                />
              </TableRow>
            );
        }
      };

      return renderRow();
    },
    [
      prepareRow,
      calculateRowProperties,
      disabled,
      showBorders,
      onMovePosition,
      dragHandlerVisible,
      dragHandlerClickable,
      shouldAutoSizeRows,
      t,
      hideRelationMarkers,
      onClickOutSide,
      tablesLayoutBorders,
      onEditPosition,
      onRowEdit,
      setEditModeConfig,
      rowSizeMode,
      skeletonFirstRowAffectedIndex,
      tableInstance,
      columns,
      onRowOut,
      handleOnRowOut,
      loadingRows,
      noIdEditableCells,
      onRowClick,
    ]
  );

  const positionMaskAreaSetting = useSetting(settings.positionMaskAreaSize());

  const selectedPositionIds = useMemo(() => {
    return Object.keys(selectedRowIds);
  }, [selectedRowIds]);

  const handleSetColumnOrder = useCallback(
    (updater: ((columnOrder: IdType<T>[]) => IdType<T>[]) | IdType<T>[]) => {
      if (typeof updater !== 'function' && handleOrderChange)
        setColumnOrder(handleOrderChange(updater));
      else setColumnOrder(updater);
    },
    [setColumnOrder, handleOrderChange]
  );

  const selectedRow = useMemo(() => {
    const values = tableInstance.rows.filter((row) =>
      selectedPositionIds.includes(row.id)
    );
    if (!(values.length === 1)) return null;
    return values[0];
  }, [selectedPositionIds, tableInstance.rows]);

  const selectedItem = selectedRow?.original;

  const maskProperties = useMemo(() => {
    if (!selectedRow) return;
    return calculateRowProperties(selectedRow, tableInstance.rows);
  }, [calculateRowProperties, selectedRow, tableInstance.rows]);

  const toggleEditedItem = (pos: T) => {
    if (!pos) {
      return;
    }
    const rowToSelect = tableInstance.rows.find(
      (row) => row.id === pos.localId
    );
    tableInstanceRef.current.toggleRowSelected(rowToSelect.id, true);
  };

  useEffect(() => {
    if (tableBodyRef.current) {
      const intinitelist =
        tableBodyRef.current.getElementsByClassName('infinite-list')[0];
      if (intinitelist && intinitelist.children[0]) {
        const child = intinitelist.children[0] as HTMLDivElement;
        if (
          classes?.innerInfiniteTable &&
          !child.classList.contains(classes?.innerInfiniteTable)
        ) {
          child.classList.add(classes?.innerInfiniteTable);
        }
        if (!fitSpace) return;

        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const height = entry.contentRect.height;
            if (tableBodyRef.current)
              tableBodyRef.current.style.height = `${Math.ceil(height)}px`;
          }
        });

        resizeObserver.observe(child);
        return () => resizeObserver.unobserve(child);
      }
    }
  }, [tableBodyRef, fitSpace, classes?.innerInfiniteTable]);

  return isMobile && renderMobileItem ? (
    <React.Fragment>
      <MobileEditTable
        items={items}
        className={classes?.mobile}
        renderMobileItem={renderMobileItem}
        renderMobileFooter={renderMobileFooter}
        toggleEditedItem={toggleEditedItem}
      />
      {selectedItem && Mask && (
        <NavigationOverlay
          initialView={{
            view: (
              <Mask
                item={selectedItem}
                onCloseClick={() => {
                  onSelectedItemIdsChange([]);
                  tableInstanceRef.current.toggleAllRowsSelected(false);
                }}
                {...maskProperties}
              />
            ),
          }}
          close={() => {
            onSelectedItemIdsChange([]);
            tableInstanceRef.current.toggleAllRowsSelected(false);
          }}
          open={selectedItem !== null}
        />
      )}
    </React.Fragment>
  ) : (
    <div
      className={clsx(styles.tableWrapper, {
        [styles.disabled]: disabled,
      })}
    >
      <div
        className={clsx(styles.outerWrap)}
        style={{ maxHeight: tableMaxHeight }}
      >
        <div
          ref={
            props.refs
              ? reactRefSetter(localScrollRef, props.refs.scroll)
              : localScrollRef
          }
          className={clsx(styles.tableWrapper, {
            [styles.disabled]: disabled,
            [styles.simpleWrapper]: !showBorders,
          })}
        >
          <div
            className={clsx(styles.table, classes?.table)}
            {...getTableProps()}
          >
            <div
              className={clsx(styles.theadWrapper, classes?.thead, {
                [styles.noBorder]: !showBorders,
              })}
            >
              <TableHeader
                flatColumns={visibleColumns as unknown as ColumnInstance[]}
                headerGroups={
                  headerGroups as unknown as HeaderGroup<ColumnAdditionalData>[]
                }
                setColumnOrder={handleSetColumnOrder}
                reordableColumns={showBorders && !allowedColumns?.length}
                resizableColumns={showBorders && !allowedColumns?.length}
                noSeperator={!showBorders}
                classes={{
                  headerWrapper: clsx(classes?.thead, styles.headerWrapper, {
                    [styles.autoScroll]: !showBorders,
                    [styles.noBorder]: !showBorders,
                  }),
                  headerRow: classes?.headerRow,
                  headerCellRoot: showBorders ? undefined : styles.border,
                  headerCellContent: styles.headerCellContent,
                }}
                styles={{
                  row: {
                    width: 'max-content',
                  },
                }}
                additionalHeaders={
                  disabled || !onMovePosition ? null : (
                    <TableCell
                      role="cell"
                      className={clsx(
                        styles['drag-handler'],
                        classes.additionalHeaders,
                        {
                          [styles['drag-handler-simple']]: !showBorders,
                        }
                      )}
                    />
                  )
                }
                groupBy={groupBy}
              />
            </div>
            <div
              ref={tableBodyRef}
              className={clsx(styles.tbody, classes?.tbody)}
              {...getTableBodyProps()}
            >
              <EditRowSelectionProvider>
                <AutoSizer>
                  {({ width, height }) => {
                    return (
                      <InfiniteList
                        width={width}
                        height={height}
                        listRef={listRef}
                        itemSize={itemSize}
                        renderRow={renderRow}
                        rows={rows}
                      />
                    );
                  }}
                </AutoSizer>
              </EditRowSelectionProvider>
            </div>

            {columns.some((col) => col.Footer) ? (
              <div
                className={clsx(styles.tfoot, classes?.tfoot, {
                  [styles.noBorder]: !showBorders,
                  [styles.noBorderHorizontal]:
                    !tablesLayoutBorders.value.horizontal || !showBorders,
                  [styles.noBorderVertical]:
                    !tablesLayoutBorders.value.vertical || !showBorders,
                })}
              >
                {headerGroups.map((headerGroup) => {
                  const props = headerGroup.getFooterGroupProps({
                    style: {
                      width: 'unset',
                      display: 'flex',
                    },
                  });
                  return (
                    <TableHeaderRow
                      isSimpleMode={!showBorders}
                      noDrag={!onMovePosition}
                      hasBorder={
                        showBorders
                          ? tablesLayoutBorders.value
                          : { horizontal: true, vertical: false }
                      }
                      disabled={disabled}
                      {...props}
                      key={props.key}
                    >
                      {headerGroup.headers.map((column) => {
                        const definition = columns[column.id as string] ?? {
                          type: 'number',
                        };
                        const footerProps = column.getFooterProps();
                        // Get exact column width to prevent sub-pixel differences
                        const exactWidthStyles = getExactColumnWidth(column);

                        return (
                          <TableCell
                            {...footerProps}
                            key={footerProps.key}
                            className={clsx(styles['table-cell'], {
                              [styles.skeleton]:
                                loadingRows.length && definition.skeleton,
                              [styles.borderTop]:
                                !showBorders && column.id !== 'PLACEHOLDER',
                              [styles.borderRight]:
                                showBorders &&
                                tablesLayoutBorders.value.vertical,
                              [styles['right-oriented']]:
                                definition.type === 'number',
                            })}
                            style={{
                              ...exactWidthStyles,
                              textAlign:
                                definition.type === 'number' ? 'right' : 'left',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {column.render('Footer')}
                          </TableCell>
                        );
                      })}
                    </TableHeaderRow>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {selectedItem && Mask && (
        <Box
          sx={{
            height: '100%',
            background: 'var(--ui01)',
          }}
        >
          {!tableRightAreaVisible && (
            <div className={styles.positionMaskToggle}>
              <ExpandButton
                icon={
                  tableRightAreaVisible ? <ChevronRight /> : <ChevronLeft />
                }
                textStart="top"
                title={t('COMMON.POSITION')}
                color="text03"
                onClick={() => setTableRightAreaVisible(!tableRightAreaVisible)}
              />
            </div>
          )}
          {tableRightAreaVisible && selectedItem && (
            <AutoSizer disableWidth>
              {({ height }) => {
                return (
                  <ResizableArea
                    minWidth={250}
                    maxWidth={1200}
                    size={{ width: positionMaskAreaSetting.value.width }}
                    onResize={(size) => positionMaskAreaSetting.set(size)}
                    handles="left"
                  >
                    <Stack direction="row" height={height}>
                      <Mask
                        item={selectedItem}
                        onCloseClick={() => setTableRightAreaVisible(false)}
                        {...maskProperties}
                      />
                    </Stack>
                  </ResizableArea>
                );
              }}
            </AutoSizer>
          )}
        </Box>
      )}
    </div>
  );
}

const EditTableInternalRef = forwardRef(EditTableInternal) as <T>(
  props: EditTableProps<T> & { ref?: React.ForwardedRef<IEditTable> }
) => ReturnType<typeof EditTableInternal>;

const EditTableWithProvider = forwardRef(
  <T,>(
    props: EditTablePropsAll<T> & { ref?: React.ForwardedRef<IEditTable> },
    ref: React.ForwardedRef<IEditTable>
  ) => {
    return (
      <EditTableProvider {...props}>
        <EditTableInternalRef ref={ref} {...(props as EditTableProps<T>)} />
      </EditTableProvider>
    );
  }
) as <T>(
  props: EditTablePropsAll<T> & { ref?: React.ForwardedRef<IEditTable> }
) => ReturnType<typeof EditTableInternal>;

export const EditTable = genericMemo(EditTableWithProvider);

interface MobileEditTableProps<T> {
  className?: string;
  renderMobileItem: EditTableProps<T>['renderMobileItem'];
  renderMobileFooter: EditTableProps<T>['renderMobileFooter'];
  items: T[];
  toggleEditedItem: (pos: T) => void;
}

function MobileEditTable<T>(props: MobileEditTableProps<T>) {
  const {
    className,
    items,
    renderMobileFooter,
    renderMobileItem,
    toggleEditedItem,
  } = props;

  return (
    <table className={className}>
      <tbody>
        {items.map((item) => {
          return (
            <React.Fragment key={(item as EditTableEntry).localId}>
              {renderMobileItem(item, toggleEditedItem, items)}
            </React.Fragment>
          );
        })}
      </tbody>
      {renderMobileFooter ? <tfoot>{renderMobileFooter(items)}</tfoot> : null}
    </table>
  );
}
